import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const formatDateTimeEastern = (value) => {
  if (!value) return 'N/A';
  try {
    const n = Number(value);
    const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(value);
    if (Number.isNaN(dt.getTime())) return 'N/A';
    return dt.toLocaleString('en-US', { timeZone: 'America/New_York' });
  } catch {
    return 'N/A';
  }
};

const formatMoneyFromCents = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${(n / 100).toFixed(2)}`;
};

const safeInt = (value, fallback = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
};

const normalizePaymentStatus = (raw) => {
  const s = String(raw || '').trim().toUpperCase();
  if (!s) return '';
  if (s === 'REFUND' || s === 'REFUNDED') return 'REFUNDED';
  if (s === 'PAID') return 'PAID';
  return s;
};

const deriveFulfillmentStatus = (order) => {
  const payment = normalizePaymentStatus(order?.payment_status || order?.paymentStatus || '');
  const rawFulfillment = normalizeStatus(order?.adminFulfillmentStatus || order?.status || order?.order_status || '');
  if (rawFulfillment === 'REFUNDED') return 'CANCELLED';
  if (payment === 'REFUNDED' && rawFulfillment !== 'SHIPPED') return 'CANCELLED';
  return rawFulfillment;
};

const isTodayEastern = (orderTimestampSeconds) => {
  if (!orderTimestampSeconds && orderTimestampSeconds !== 0) return false;
  try {
    const n = Number(orderTimestampSeconds);
    const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(orderTimestampSeconds);
    if (Number.isNaN(dt.getTime())) return false;
    const today = new Date();

    const a = dt.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    const b = today.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    return a === b;
  } catch {
    return false;
  }
};

const presetKeyToLabel = (key) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === '1d') return '1 day (today)';
  if (k === '7d') return '7 days';
  if (k === '1m') return '1 month';
  if (k === '6m') return '6 months';
  if (k === 'ytd') return 'this year';
  if (k === 'all') return 'ALL';
  if (k === 'custom') return 'CUSTOM';
  return key ? String(key) : '7 days';
};

const presetKeyToDays = (key, customDays) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === '1d') return 1;
  if (k === '7d') return 7;
  if (k === '1m') return 31;
  if (k === '6m') return 183;
  if (k === 'all') return 365;
  if (k === 'ytd') {
    try {
      const now = new Date();
      const jan1 = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      const diffMs = now.getTime() - jan1.getTime();
      const days = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      return Math.min(365, days);
    } catch {
      return 365;
    }
  }
  if (k === 'custom') {
    const n = Number(customDays);
    if (Number.isFinite(n) && n > 0) return Math.min(365, Math.floor(n));
    return 7;
  }
  return 7;
};

const normalizeStatus = (raw) => {
  const s = String(raw || '').trim().toUpperCase();
  if (!s) return '';
  if (s === 'CANCELED') return 'CANCELLED';
  if (s === 'CANCEL') return 'CANCELLED';
  return s;
};

export default function AdminOrdersQueue() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [products, setProducts] = useState([]);
  const productsById = useMemo(() => {
    const m = {};
    for (const p of products || []) {
      const pid = String(p?.ProductID || '').trim();
      if (!pid) continue;
      m[pid] = p;
    }
    return m;
  }, [products]);

  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);

  const [cursor, setCursor] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  const [filter, setFilter] = useState('ALL');
  const [orderSourceFilter, setOrderSourceFilter] = useState(() => {
    try {
      return localStorage.getItem('adminOrdersQueue_orderSourceFilter') || 'ALL';
    } catch {
      return 'ALL';
    }
  });
  const [deliveryChannelFilter, setDeliveryChannelFilter] = useState(() => {
    try {
      return localStorage.getItem('adminOrdersQueue_deliveryChannelFilter') || 'ALL';
    } catch {
      return 'ALL';
    }
  });

  const [dateMode, setDateMode] = useState('preset');
  const [presetKey, setPresetKey] = useState('7d');
  const [customDays, setCustomDays] = useState('7');
  const [lookbackDays, setLookbackDays] = useState(7);
  const [searchTerm, setSearchTerm] = useState('');

  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [markOrder, setMarkOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [force, setForce] = useState(false);
  const [submittingMark, setSubmittingMark] = useState(false);

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Orders/Reports');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }
        setUsername(user.username || user.attributes?.email || 'Admin');
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const markAsPaid = async (order) => {
    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) return;
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const ok = window.confirm(`Mark order ${orderNumber} as PAID?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Marking paid…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/mark-paid`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to mark paid'));

      await fetchQueue({ reset: true, quiet: true });
      setStatusMessage(`Order ${orderNumber} marked PAID.`);
    } catch (e) {
      setError(e?.message || 'Failed to mark paid');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!API_BASE_URL) return;
    let cancelled = false;
    const run = async () => {
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const res = await fetch(`${API_BASE_URL}/admin/products`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;
        const list = Array.isArray(data?.products) ? data.products : [];
        if (!cancelled) setProducts(list);
      } catch {
        // ignore
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loading]);

  const fetchQueue = async ({ reset, quiet } = { reset: false, quiet: false }) => {
    if (!quiet) {
      setError('');
      setStatusMessage('');
    }

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    try {
      if (reset) {
        setBusy(true);
      } else {
        setLoadingMore(true);
      }
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const url = new URL(`${API_BASE_URL}/admin/fulfillment/backlog`);
      const want = String(filter || 'ALL').trim().toUpperCase();
      const normalizedWant = want === 'CANCELED' ? 'CANCELLED' : want;

      const statusParam = normalizedWant === 'ALL' || normalizedWant === 'ACTIVE' ? '' : normalizedWant.toLowerCase();
      const shouldUseAllBucket = normalizedWant === 'ALL' || normalizedWant === 'ACTIVE' || normalizedWant === 'CANCELLED' || normalizedWant === 'SHIPPED';
      url.searchParams.set('bucket', shouldUseAllBucket ? 'all' : 'backlog');
      if (statusParam && shouldUseAllBucket) {
        url.searchParams.set('status', statusParam);
      }
      if (statusParam && normalizedWant === 'PROCESSING') {
        url.searchParams.set('status', statusParam);
      }
      url.searchParams.set('limit', '50');

      if (!reset && cursor) {
        url.searchParams.set('cursor', cursor);
      }

      const strictToday = dateMode === 'today' || (dateMode === 'preset' && String(presetKey || '').trim().toLowerCase() === '1d');
      if (strictToday) {
        const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        url.searchParams.set('start', todayKey);
        url.searchParams.set('end', todayKey);
      } else {
        const now = Math.floor(Date.now() / 1000);
        const start = Math.max(0, now - Number(lookbackDays || 7) * 86400);
        url.searchParams.set('start', String(start));
        url.searchParams.set('end', String(now));
      }

      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load Orders Queue');

      const list = Array.isArray(data?.orders) ? data.orders : [];

      const nextOrders = reset ? list : [...(orders || []), ...list];
      const sortedNextOrders = [...nextOrders].sort((a, b) => {
        const at = Number(a?.order_timestamp || 0);
        const bt = Number(b?.order_timestamp || 0);
        if (bt !== at) return bt - at;
        return String(b?.order_number || '').localeCompare(String(a?.order_number || ''));
      });
      setOrders(sortedNextOrders);
      setCursor(String(data?.next_cursor || ''));
      if (!quiet) {
        const strictToday = dateMode === 'today' || (dateMode === 'preset' && String(presetKey || '').trim().toLowerCase() === '1d');
        const wantFilter = String(filter || 'ALL').trim().toUpperCase();
        const term = String(searchTerm || '').trim().toLowerCase();
        const shown = (sortedNextOrders || []).filter((o) => {
          if (strictToday && !isTodayEastern(o?.order_timestamp)) return false;
          const st = deriveFulfillmentStatus(o);
          if (wantFilter === 'ACTIVE' && (st === 'CANCELLED' || normalizePaymentStatus(o?.payment_status) === 'REFUNDED')) return false;
          if (wantFilter !== 'ALL' && wantFilter !== 'ACTIVE' && st !== wantFilter) return false;
          if (!term) return true;
          const orderNumber = String(o?.order_number || '').toLowerCase();
          const name = String(o?.customer_name || '').toLowerCase();
          const email = String(o?.customer_email || '').toLowerCase();
          const phone = String(o?.customer_phone || '').toLowerCase();
          return orderNumber.includes(term) || name.includes(term) || email.includes(term) || phone.includes(term);
        });
        setStatusMessage(`Showing ${shown.length} order(s).`);
      }
    } catch (e) {
      if (reset) {
        setOrders([]);
        setCursor('');
      }
      if (!quiet) {
        setError(e?.message || 'Failed to load Orders Queue');
      }
    } finally {
      setBusy(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    fetchQueue({ reset: true });
  }, [loading]);

  useEffect(() => {
    if (loading) return;
    fetchQueue({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, dateMode, presetKey, customDays, lookbackDays]);

  useEffect(() => {
    if (loading) return;
    if (dateMode !== 'preset') return;
    const next = presetKeyToDays(presetKey, customDays);
    if (next !== lookbackDays) setLookbackDays(next);
  }, [loading, dateMode, presetKey, customDays, lookbackDays]);

  useEffect(() => {
    if (dateMode === 'today') {
      setPresetKey('1d');
      setLookbackDays(1);
    }
  }, [dateMode]);

  // Persist order source filter
  useEffect(() => {
    try {
      localStorage.setItem('adminOrdersQueue_orderSourceFilter', orderSourceFilter);
    } catch {
      // ignore
    }
  }, [orderSourceFilter]);

  // Persist delivery channel filter
  useEffect(() => {
    try {
      localStorage.setItem('adminOrdersQueue_deliveryChannelFilter', deliveryChannelFilter);
    } catch {
      // ignore
    }
  }, [deliveryChannelFilter]);

  // Reset delivery channel filter when order source changes away from Otter
  useEffect(() => {
    if (orderSourceFilter !== 'otter' && deliveryChannelFilter !== 'ALL') {
      setDeliveryChannelFilter('ALL');
    }
  }, [orderSourceFilter, deliveryChannelFilter]);

  const filtered = useMemo(() => {
    const want = String(filter || 'ALL').trim().toUpperCase();
    const term = String(searchTerm || '').trim().toLowerCase();
    const strictToday = dateMode === 'today' || (dateMode === 'preset' && String(presetKey || '').trim().toLowerCase() === '1d');
    const wantSource = String(orderSourceFilter || 'ALL').trim().toLowerCase();
    const wantChannel = String(deliveryChannelFilter || 'ALL').trim().toLowerCase();

    return (orders || []).filter((o) => {
      if (strictToday && !isTodayEastern(o?.order_timestamp)) return false;
      const st = deriveFulfillmentStatus(o);
      const pay = normalizePaymentStatus(o?.payment_status);
      if (want === 'ACTIVE' && (st === 'CANCELLED' || pay === 'REFUNDED')) return false;
      if (want !== 'ALL' && want !== 'ACTIVE' && st !== want) return false;

      // Filter by order source
      if (wantSource !== 'all') {
        const orderSource = String(o?.order_source || '').trim().toLowerCase();
        if (wantSource === 'otter' && orderSource !== 'otter') return false;
        if (wantSource === 'storefront' && orderSource === 'otter') return false;
      }

      // Filter by delivery channel
      if (wantChannel !== 'all') {
        const deliveryChannel = String(o?.delivery_channel || '').trim().toLowerCase();
        if (deliveryChannel !== wantChannel) return false;
      }

      if (!term) return true;
      const orderNumber = String(o?.order_number || '').toLowerCase();
      const name = String(o?.customer_name || '').toLowerCase();
      const email = String(o?.customer_email || '').toLowerCase();
      const phone = String(o?.customer_phone || '').toLowerCase();
      const otterOrderId = String(o?.otter_order_id || '').toLowerCase();
      const deliveryChannel = String(o?.delivery_channel || '').toLowerCase();
      return orderNumber.includes(term) || name.includes(term) || email.includes(term) || phone.includes(term) || otterOrderId.includes(term) || deliveryChannel.includes(term);
    });
  }, [orders, filter, searchTerm, dateMode, presetKey, orderSourceFilter, deliveryChannelFilter]);

  const hasMore = useMemo(() => Boolean(cursor), [cursor]);

  const openMarkModal = (order) => {
    setMarkOrder(order);
    setTrackingNumber(String(order?.tracking_number || ''));
    setCarrier(String(order?.carrier || ''));
    setSendEmail(true);
    setForce(false);
    setMarkModalOpen(true);
  };

  const closeMarkModal = () => {
    setMarkModalOpen(false);
    setMarkOrder(null);
    setTrackingNumber('');
    setCarrier('');
    setSendEmail(true);
    setForce(false);
    setSubmittingMark(false);
  };

  const submitMarkShipped = async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    const orderNumber = String(markOrder?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order number.');
      return;
    }

    const tn = String(trackingNumber || '').trim();
    if (!tn) {
      setError('Tracking number is required.');
      return;
    }

    try {
      setSubmittingMark(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/mark-shipped`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          tracking_number: tn,
          carrier: String(carrier || '').trim(),
          send_email: sendEmail,
          force,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to mark shipped'));

      setStatusMessage(`Order ${orderNumber} marked shipped (${data?.email_status || 'email skipped'}).`);
      closeMarkModal();
      await fetchQueue({ reset: true, quiet: true });
    } catch (e) {
      setError(e?.message || 'Failed to mark shipped');
    } finally {
      setSubmittingMark(false);
    }
  };

  const markProcessing = async (order) => {
    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) return;
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    const ok = window.confirm(`Mark order ${orderNumber} as Processing?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Updating…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/mark-processing`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber, reason: 'Processing in admin portal' }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to mark Processing'));

      await fetchQueue({ reset: true, quiet: true });
      setStatusMessage(`Order ${orderNumber} marked Processing.`);
    } catch (e) {
      setError(e?.message || 'Failed to mark Processing');
    } finally {
      setBusy(false);
    }
  };

  const cancelRefund = async (order) => {
    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) return;
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    const ok = window.confirm(`Cancel + refund order ${orderNumber}?\n\nThis will attempt a Stripe refund.`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Cancelling + refunding…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber, reason: 'Cancelled in admin portal' }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to cancel/refund'));

      const refundId = String(data?.refund_id || '');
      await fetchQueue({ reset: true, quiet: true });
      setStatusMessage(`Order ${orderNumber} cancelled + refunded.${refundId ? ` Refund: ${refundId}` : ''}`);
    } catch (e) {
      setError(e?.message || 'Failed to cancel/refund');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-receipt"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div className="admin-header-welcome">
            <h1>Orders Queue</h1>
            <p>ECOM paid orders (read-only payment)</p>
          </div>
        </div>

        <div className="admin-header-right">
          <div className="admin-user-info">
            <span className="admin-username">{username}</span>
            <span className="admin-role">Administrator</span>
          </div>
          <button
            onClick={async () => {
              try {
                await Auth.signOut();
              } catch {
                // ignore
              }
              navigate('/admin/login');
            }}
            className="admin-logout-btn"
          >
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <AdminNavTabs />

      <main className="admin-main">
        <div ref={statusRef} tabIndex="-1" />
        {error ? <div className="admin-error">{error}</div> : null}
        {statusMessage ? <div className="admin-status">{statusMessage}</div> : null}

        <section className="admin-card">
          <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ margin: 0 }}>Queue</h2>
              <div className="field-hint">Payment is always treated as PAID for ECOM.</div>
            </div>
          </div>

          <div className="pos-toolbar" style={{ marginBottom: 16 }}>
            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Fulfillment</label>
              <select className="pos-input" value={filter} onChange={(e) => setFilter(String(e.target.value || 'ALL'))} disabled={busy || loadingMore} style={{ minWidth: 200 }}>
                <option value="ALL">ALL</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="PROCESSING">PROCESSING</option>
                <option value="SHIPPED">SHIPPED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Order Source</label>
              <select className="pos-input" value={orderSourceFilter} onChange={(e) => setOrderSourceFilter(String(e.target.value || 'ALL'))} disabled={busy || loadingMore} style={{ minWidth: 160 }}>
                <option value="ALL">ALL</option>
                <option value="otter">Otter</option>
                <option value="storefront">Storefront</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Delivery Channel</label>
              <select className="pos-input" value={deliveryChannelFilter} onChange={(e) => setDeliveryChannelFilter(String(e.target.value || 'ALL'))} disabled={busy || loadingMore || orderSourceFilter !== 'otter'} style={{ minWidth: 160 }}>
                <option value="ALL">ALL</option>
                <option value="doordash">DoorDash</option>
                <option value="ubereats">Uber Eats</option>
                <option value="grubhub">GrubHub</option>
                <option value="postmates">Postmates</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Date</label>
              <div className="pos-chip-row">
                <button type="button" className={`pos-chip ${dateMode === 'today' ? 'active' : ''}`} onClick={() => setDateMode('today')} disabled={busy || loadingMore}>
                  TODAY
                </button>
                <button type="button" className={`pos-chip ${dateMode === 'preset' ? 'active' : ''}`} onClick={() => setDateMode('preset')} disabled={busy || loadingMore}>
                  PRESET
                </button>
              </div>
            </div>

            {dateMode === 'preset' ? (
              <div className="pos-toolbar-group">
                <label className="pos-toolbar-label">Range</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select className="pos-input" value={presetKey} onChange={(e) => setPresetKey(String(e.target.value || '7d'))} disabled={busy || loadingMore} style={{ minWidth: 220 }}>
                    <option value="1d">{presetKeyToLabel('1d')}</option>
                    <option value="7d">{presetKeyToLabel('7d')}</option>
                    <option value="1m">{presetKeyToLabel('1m')}</option>
                    <option value="6m">{presetKeyToLabel('6m')}</option>
                    <option value="ytd">{presetKeyToLabel('ytd')}</option>
                    <option value="all">{presetKeyToLabel('all')}</option>
                    <option value="custom">{presetKeyToLabel('custom')}</option>
                  </select>
                  {String(presetKey || '').trim().toLowerCase() === 'custom' ? (
                    <input
                      className="pos-input"
                      type="number"
                      min={1}
                      max={365}
                      value={customDays}
                      onChange={(e) => setCustomDays(String(e.target.value || '7'))}
                      disabled={busy || loadingMore}
                      style={{ minWidth: 140 }}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="pos-toolbar-group" style={{ minWidth: 240 }}>
              <label className="pos-toolbar-label">Search</label>
              <input
                className="pos-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Order #, name, phone, email, Otter ID, channel"
                disabled={busy || loadingMore}
              />
            </div>

            <div className="pos-toolbar-actions">
              <button type="button" className="btn-primary" onClick={() => fetchQueue({ reset: true })} disabled={busy || loadingMore}>
                Refresh
              </button>
              <button type="button" className="btn-secondary" onClick={() => fetchQueue({ reset: false })} disabled={busy || loadingMore || !hasMore}>
                Load more
              </button>
            </div>
          </div>

          {!filtered.length ? (
            <div className="admin-empty">
              <p>No orders.</p>
              <div className="field-hint">Try a wider date range.</div>
            </div>
          ) : (
            <div className="pos-order-list">
              {filtered.map((o) => {
                const orderNumber = String(o?.order_number || '').trim();
                const paidAt = formatDateTimeEastern(o?.paid_at || o?.order_timestamp);
                const totalCents = safeInt(o?.amount_total ?? o?.amount_total_cents, 0);
                const subtotalCents = safeInt(o?.amount_subtotal ?? o?.subtotal_cents, 0);
                const taxCents = safeInt(o?.amount_tax ?? o?.tax_cents, 0);
                const shippingCents = safeInt(o?.amount_shipping ?? o?.shipping_cents ?? o?.shipping_amount_cents, 0);
                const discountCentsRaw = safeInt(o?.discount_cents ?? o?.discount_amount_cents ?? o?.customerSubmittedSnapshot?.discount_cents, 0);
                const discountCents = Math.max(0, Math.min(discountCentsRaw, subtotalCents));
                const taxableSubtotalCents = Math.max(0, subtotalCents - discountCents);
                const hasBreakdown = o?.amount_subtotal != null || o?.amount_tax != null || o?.amount_shipping != null || discountCents > 0;
                const pct = taxableSubtotalCents > 0 ? Math.round((taxCents / taxableSubtotalCents) * 10000) / 100 : 0;
                const total = formatMoneyFromCents(totalCents);
                const customerName = String(o?.customer_name || '').trim();
                const customerEmail = String(o?.customer_email || '').trim();
                const payment = normalizePaymentStatus(o?.payment_status) || 'PAID';
                const fulfillment = deriveFulfillmentStatus(o);
                const tracking = String(o?.tracking_number || '').trim();

                const firstItem = Array.isArray(o?.items) && o.items.length ? o.items[0] : null;
                const firstPid = String(firstItem?.ProductID || firstItem?.productId || firstItem?.product_id || '').trim();
                const summaryThumb = String(firstItem?.ImageURL || firstItem?.image_url || firstItem?.imageUrl || productsById[firstPid]?.ImageURL || '').trim();

                const current = deriveFulfillmentStatus(o);
                const isProcessing = current === 'PROCESSING';
                const isShipped = current === 'SHIPPED';
                const isCancelled = current === 'CANCELLED';
                const isRefunded = payment === 'REFUNDED';
                const canMarkPaid = !isCancelled && payment !== 'PAID' && payment !== 'REFUNDED';

                const disableProcessing = busy || loadingMore || isRefunded || isProcessing || isShipped || isCancelled;
                const disableShipped = busy || loadingMore || isRefunded || isShipped || isCancelled;
                const disableCancel = busy || loadingMore || isRefunded || isCancelled || isShipped;
                const disableMarkPaid = busy || loadingMore || isRefunded || isCancelled || payment === 'PAID';

                return (
                  <details key={`${orderNumber}-${String(o?.order_timestamp || '')}`} className="pos-order-disclosure">
                    <summary className="pos-order-summary">
                      <div className="pos-order-summary-main">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {summaryThumb ? (
                            <img
                              src={summaryThumb}
                              alt=""
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 10,
                                objectFit: 'cover',
                                border: '1px solid rgba(15, 23, 42, 0.10)',
                              }}
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                try {
                                  e.currentTarget.style.display = 'none';
                                } catch {
                                }
                              }}
                            />
                          ) : null}
                        <button
                          type="button"
                          className="pos-order-summary-title-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          Order {orderNumber || '—'}
                        </button>
                        </div>
                        <div className="pos-order-summary-sub">Paid: {paidAt}</div>
                      </div>

                      <div className="pos-order-summary-customer">
                        <div className="pos-order-summary-customer-name">{customerName || '—'}</div>
                        {customerEmail ? <div className="pos-order-summary-customer-phone">{customerEmail}</div> : null}
                      </div>

                      <div className="pos-order-summary-badges">
                        <span className={`pos-badge ${payment === 'REFUNDED' ? 'pos-badge-refunded' : 'pos-badge-paid'}`}>{payment}</span>
                        <span className="pos-badge pos-badge-neutral">{fulfillment || '—'}</span>
                        {o?.order_source === 'otter' ? (
                          <span className="pos-badge" style={{ backgroundColor: '#FF6B35', color: 'white', fontWeight: 600 }}>
                            🦦 OTTER
                          </span>
                        ) : null}
                        {o?.delivery_channel ? (
                          <span className="pos-badge pos-badge-neutral" style={{ fontSize: '0.75rem' }}>
                            {o.delivery_channel}
                          </span>
                        ) : null}
                      </div>

                      <div className="pos-order-summary-chevron" aria-hidden="true" />
                    </summary>

                    <div className="pos-order-details">
                      <div className="pos-order-card">
                        <div className="pos-order-meta">
                          <div>
                            <div className="pos-order-meta-label">{hasBreakdown ? 'Estimated total' : 'Total'}</div>
                            <div className="pos-order-meta-value pos-order-total-highlight">{total}</div>
                            {hasBreakdown ? (
                              <div className="pos-order-total-breakdown">
                                <div className="pos-order-total-row">
                                  <span>Subtotal</span>
                                  <span>{formatMoneyFromCents(subtotalCents)}</span>
                                </div>
                                {discountCents ? (
                                  <div className="pos-order-total-row">
                                    <span>Discount</span>
                                    <span>-{formatMoneyFromCents(discountCents)}</span>
                                  </div>
                                ) : null}
                                <div className="pos-order-total-row">
                                  <span>Estimated tax ({pct || 0}%)</span>
                                  <span>{formatMoneyFromCents(taxCents)}</span>
                                </div>
                                {shippingCents ? (
                                  <div className="pos-order-total-row">
                                    <span>Shipping</span>
                                    <span>{formatMoneyFromCents(shippingCents)}</span>
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                          <div>
                            <div className="pos-order-meta-label">Payment</div>
                            <div className="pos-order-meta-value">{payment}</div>
                          </div>
                          <div>
                            <div className="pos-order-meta-label">Fulfillment</div>
                            <div className="pos-order-meta-value">{fulfillment || '—'}</div>
                            {tracking ? <div className="field-hint">Tracking: {tracking}</div> : null}
                          </div>
                        </div>

                        {o?.order_source === 'otter' ? (
                          <div className="pos-order-meta" style={{ marginTop: 12, padding: 12, backgroundColor: '#FFF4ED', borderRadius: 8, border: '1px solid #FF6B35' }}>
                            <div style={{ fontWeight: 700, color: '#FF6B35', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>🦦</span>
                              <span>Otter Delivery Order</span>
                              {o?.delivery_channel ? <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>via {o.delivery_channel}</span> : null}
                            </div>
                            {o?.delivery_address ? (
                              <div style={{ marginBottom: 8 }}>
                                <div className="pos-order-meta-label">Delivery Address</div>
                                <div className="pos-order-meta-value" style={{ fontSize: '0.9rem' }}>
                                  {o.delivery_address.street || ''}
                                  {o.delivery_address.street2 ? `, ${o.delivery_address.street2}` : ''}
                                  <br />
                                  {o.delivery_address.city || ''}, {o.delivery_address.state || ''} {o.delivery_address.zip || ''}
                                </div>
                              </div>
                            ) : null}
                            {o?.delivery_instructions ? (
                              <div style={{ marginBottom: 8 }}>
                                <div className="pos-order-meta-label">Delivery Instructions</div>
                                <div className="pos-order-meta-value" style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                                  {o.delivery_instructions}
                                </div>
                              </div>
                            ) : null}
                            {o?.requested_delivery_time ? (
                              <div>
                                <div className="pos-order-meta-label">Requested Delivery Time</div>
                                <div className="pos-order-meta-value" style={{ fontSize: '0.9rem' }}>
                                  {formatDateTimeEastern(o.requested_delivery_time)}
                                  {(() => {
                                    const now = Math.floor(Date.now() / 1000);
                                    const requestedTime = Number(o.requested_delivery_time);
                                    const minutesUntil = Math.floor((requestedTime - now) / 60);
                                    if (minutesUntil > 0 && minutesUntil <= 15) {
                                      return (
                                        <span style={{ marginLeft: 8, color: '#DC2626', fontWeight: 600, fontSize: '0.85rem' }}>
                                          ⚠️ URGENT ({minutesUntil} min)
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            ) : null}
                            {o?.otter_order_id ? (
                              <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#64748B' }}>
                                Otter Order ID: {o.otter_order_id}
                              </div>
                            ) : null}
                            {o?.is_modified ? (
                              <div style={{ marginTop: 8, padding: 8, backgroundColor: '#FEF3C7', borderRadius: 6, border: '1px solid #F59E0B' }}>
                                <div style={{ fontWeight: 600, color: '#D97706', fontSize: '0.85rem', marginBottom: 4 }}>
                                  ⚠️ Order Modified
                                </div>
                                {o?.last_modified_at ? (
                                  <div style={{ fontSize: '0.75rem', color: '#92400E' }}>
                                    Last modified: {formatDateTimeEastern(o.last_modified_at)}
                                  </div>
                                ) : null}
                                {o?.modification_history && o.modification_history.length > 0 ? (
                                  <div style={{ fontSize: '0.75rem', color: '#92400E', marginTop: 4 }}>
                                    {o.modification_history.length} modification{o.modification_history.length > 1 ? 's' : ''} recorded
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            {o?.is_cancelled ? (
                              <div style={{ marginTop: 8, padding: 8, backgroundColor: '#FEE2E2', borderRadius: 6, border: '1px solid #DC2626' }}>
                                <div style={{ fontWeight: 600, color: '#DC2626', fontSize: '0.85rem', marginBottom: 4 }}>
                                  ❌ Order Cancelled
                                </div>
                                {o?.cancelled_at ? (
                                  <div style={{ fontSize: '0.75rem', color: '#991B1B' }}>
                                    Cancelled: {formatDateTimeEastern(o.cancelled_at)}
                                  </div>
                                ) : null}
                                {o?.cancellation_reason ? (
                                  <div style={{ fontSize: '0.75rem', color: '#991B1B', marginTop: 4, fontStyle: 'italic' }}>
                                    Reason: {o.cancellation_reason}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="pos-order-links" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <button type="button" className="btn-secondary" onClick={() => markProcessing(o)} disabled={disableProcessing}>
                            Processing
                          </button>
                          {canMarkPaid ? (
                            <button type="button" className="btn-secondary" onClick={() => markAsPaid(o)} disabled={disableMarkPaid}>
                              Mark as Paid
                            </button>
                          ) : null}
                          <button type="button" className="btn-secondary" onClick={() => openMarkModal(o)} disabled={disableShipped}>
                            Mark shipped
                          </button>
                          <button type="button" className="btn-danger" onClick={() => cancelRefund(o)} disabled={disableCancel}>
                            Cancel + refund
                          </button>
                        </div>

                        {Array.isArray(o?.items) && o.items.length > 0 ? (
                          <div className="pos-order-items" style={{ marginTop: 14 }}>
                            <div style={{ fontWeight: 900, color: '#0f172a' }}>Items</div>
                            <ul>
                              {o.items.slice(0, 20).map((it, idx) => {
                                const pid = String(it?.ProductID || it?.productId || it?.product_id || '').trim();
                                const imageUrl = String(it?.ImageURL || it?.image_url || it?.imageUrl || productsById[pid]?.ImageURL || '').trim();
                                const name = String(it?.name || it?.ProductName || productsById[pid]?.ProductName || 'Item');
                                const qty = String(it?.Quantity || it?.quantity || it?.qty || 1);
                                return (
                                  <li key={`ecom-items-${orderNumber}-${idx}`}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      {imageUrl ? (
                                        <img
                                          src={imageUrl}
                                          alt=""
                                          style={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: 10,
                                            objectFit: 'cover',
                                            border: '1px solid rgba(15, 23, 42, 0.10)',
                                          }}
                                          loading="lazy"
                                          referrerPolicy="no-referrer"
                                          onError={(e) => {
                                            try {
                                              e.currentTarget.style.display = 'none';
                                            } catch {
                                            }
                                          }}
                                        />
                                      ) : null}
                                      <span>
                                        {name} x{qty}
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {markModalOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3>Mark shipped</h3>
              <button type="button" className="modal-close" onClick={closeMarkModal} disabled={submittingMark}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Order</label>
                <div className="field-hint">{String(markOrder?.order_number || '—')}</div>
              </div>
              <div className="form-row">
                <label htmlFor="tracking">Tracking number</label>
                <input id="tracking" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} disabled={submittingMark} />
              </div>
              <div className="form-row">
                <label htmlFor="carrier">Carrier (optional)</label>
                <input id="carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} disabled={submittingMark} />
              </div>
              <div className="form-row" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} disabled={submittingMark} />
                  Send customer email
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} disabled={submittingMark} />
                  Force
                </label>
              </div>
              <div className="form-row" style={{ marginTop: 12 }}>
                <button type="button" className="btn-primary" onClick={submitMarkShipped} disabled={submittingMark}>
                  {submittingMark ? 'Saving…' : 'Mark shipped'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
